import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { subscribeAgentStream, type WaitingChat } from '../connect/agentStream.js';
import { primeRingtone, requestRingtone, releaseRingtone } from '../connect/ringtone.js';
import { WaitingChatAlert } from './WaitingChatAlert.js';

/**
 * App-wide incoming-chat alerting. Mounted once in the authenticated shell (Layout)
 * so an agent is alerted with a looping ringtone + banner on ANY page — Dashboard,
 * Conversations, etc. — not only the Workspace. Rings until the agent opens the chat
 * (silences + focuses the Connect softphone) or passes it (leaves it queued for another
 * rep); clears for everyone when the chat is accepted or ends.
 */
export function AgentNotifications() {
  const [waiting, setWaiting] = useState<WaitingChat[]>([]);
  const [acknowledged, setAcknowledged] = useState<Set<string>>(() => new Set());
  const navigate = useNavigate();

  // Subscribe to the realtime stream for the whole authenticated session.
  useEffect(() => {
    const off = subscribeAgentStream({
      onWaiting: (chat) =>
        setWaiting((prev) =>
          prev.some((c) => c.contactId === chat.contactId) ? prev : [...prev, chat],
        ),
      onCleared: (contactId) => {
        setWaiting((prev) => prev.filter((c) => c.contactId !== contactId));
        setAcknowledged((prev) => {
          if (!prev.has(contactId)) return prev;
          const next = new Set(prev);
          next.delete(contactId);
          return next;
        });
      },
    });
    return off;
  }, []);

  // Unlock the WebAudio ringtone on the first user gesture anywhere in the app, so it
  // can ring later without a direct click on the alert (autoplay policy).
  useEffect(() => {
    const prime = (): void => primeRingtone();
    window.addEventListener('pointerdown', prime, { once: true });
    return () => window.removeEventListener('pointerdown', prime);
  }, []);

  const unacked = waiting.filter((c) => !acknowledged.has(c.contactId));
  useEffect(() => {
    if (unacked.length > 0) requestRingtone('sse');
    else releaseRingtone('sse');
  }, [unacked.length]);
  useEffect(() => () => releaseRingtone('sse'), []);

  const acknowledge = (contactId: string): void =>
    setAcknowledged((prev) => {
      const next = new Set(prev);
      next.add(contactId);
      return next;
    });

  // Open the waiting chat's CRM conversation — that page mounts <ChatComposer>, which
  // joins the chat as the CUSTOM_BOT "Support" participant so the agent can actually
  // reply. (The Connect CCP softphone can't handle chat and is reserved for voice.)
  const openWaiting = (chat: WaitingChat): void => {
    acknowledge(chat.contactId);
    navigate(`/conversations/${chat.conversationId}`);
  };
  const passWaiting = (contactId: string): void => acknowledge(contactId);

  if (unacked.length === 0) return null;

  return (
    <div className="agent-alerts" role="region" aria-label="Incoming chats">
      {unacked.map((c) => (
        <WaitingChatAlert
          key={c.contactId}
          chat={c}
          onOpen={() => openWaiting(c)}
          onPass={() => passWaiting(c.contactId)}
        />
      ))}
    </div>
  );
}
